// TanStack Query hooks backing the admin home screen. Both queries hot-poll
// every 5s while the tab is visible (matrix-stats convention) so WIN/LOSS
// settles show up "live" — the backend has no admin-wide trade.settled SSE
// topic, so short polling is the pragmatic push substitute (operator
// 2026-07-05). Returning to the tab refetches immediately instead of waiting
// a full tick (settles that landed while hidden paint right away).
import { useQuery, type UseQueryResult } from "@/lib/query";
import { usePageResumeRefetch } from "@/lib/query/use-page-resume-refetch";
import {
  fetchAdminDashboard,
  fetchAdminRecentTrades,
  fetchAdminTopPlayers,
  type AdminDashboardSnapshot,
  type AdminTradeListPage,
  type AdminTopPlayersPage,
} from "../api";
import { adminDashboardKeys } from "../keys";

const REFRESH_INTERVAL_MS = 5_000;

/** Aggregated KPIs for the admin home screen. Hot-polls 5s while visible. */
export function useAdminDashboardSnapshot(): UseQueryResult<AdminDashboardSnapshot> {
  const query = useQuery<AdminDashboardSnapshot>({
    queryKey: adminDashboardKeys.snapshot(),
    queryFn: fetchAdminDashboard,
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
  usePageResumeRefetch(query.refetch);
  return query;
}

/** Last `limit` trades across all users. Hot-polls 5s while visible. */
export function useAdminRecentTrades(
  limit = 10,
): UseQueryResult<AdminTradeListPage> {
  const query = useQuery<AdminTradeListPage>({
    queryKey: adminDashboardKeys.recentTrades(limit),
    queryFn: () => fetchAdminRecentTrades({ limit }),
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
  usePageResumeRefetch(query.refetch);
  return query;
}

/** Top 10 players of the day (most trades today + P&L). Hot-polls 5s while
 *  visible so the ranking climbs live as trades settle. */
export function useAdminTopPlayers(): UseQueryResult<AdminTopPlayersPage> {
  const query = useQuery<AdminTopPlayersPage>({
    queryKey: adminDashboardKeys.topPlayers(),
    queryFn: fetchAdminTopPlayers,
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
  usePageResumeRefetch(query.refetch);
  return query;
}
